// src/pages/AnonymousIdPolicyPage.js
import React, { useEffect } from 'react';
import { useAnonConsent } from '../context/AnonConsentContext';
import { ensureRecaptchaReady, getRecaptchaToken } from '../lib/recaptcha';

const RECAPTCHA_ACTION = 'anon_consent';

export default function AnonymousIdPolicyPage() {
  const { consented, loading, updating, error, setConsent } = useAnonConsent();
  const [message, setMessage] = React.useState('');
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    setMessage('');
  }, [consented]);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    ensureRecaptchaReady().catch(() => {
      if (!cancelled) {
        setMessage('reCAPTCHA failed to load. Please disable extensions and retry.');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  const runRecaptcha = React.useCallback(async () => {
    if (!siteKey) return null;
    try {
      return await getRecaptchaToken(RECAPTCHA_ACTION);
    } catch (err) {
      throw new Error(err?.message || 'reCAPTCHA challenge failed. Please try again.');
    }
  }, [siteKey]);

  const handleOptOut = async () => {
    setMessage('');
    try {
      const token = await runRecaptcha();
      const ok = await setConsent(false, { recaptchaToken: token });
      if (ok) {
        setMessage('Opt-out saved. You can continue browsing in read-only mode.');
      }
    } catch (err) {
      setMessage(err?.message || 'Failed to complete reCAPTCHA.');
    }
  };

  const handleOptIn = async () => {
    setMessage('');
    try {
      const token = await runRecaptcha();
      const ok = await setConsent(true, { recaptchaToken: token });
      if (ok) {
        setMessage('Opt-out cleared. A new anonymous user ID will be issued on your next action.');
      }
    } catch (err) {
      setMessage(err?.message || 'Failed to complete reCAPTCHA.');
    }
  };

  return (
    <div className="card" style={{ maxWidth: 720, margin: '12px auto', textAlign: 'left' }}>
      <h2 style={{ marginTop: 0 }}>Anonymous User ID Policy</h2>
      <p>
        getSurvey assigns a pseudonymous <strong>anonymous user ID</strong> so that core features work without a full
        account. This ID is generated on the server using non-personal signals such as IP address and browser headers,
        then hashed and signed. We do <strong>not</strong> store raw fingerprint data, and the anonymous ID is never shared with
        third parties.
      </p>
      <p>
        The anonymous ID enables key functionality, including:
      </p>
      <ul>
        <li>Submitting survey responses (one response per anonymous ID)</li>
        <li>Liking surveys and posts</li>
        <li>Posting and replying in discussions</li>
        <li>Creating new surveys and saving drafts</li>
      </ul>
      <p>
        If you prefer not to be tracked via an anonymous ID, you may opt out. When opted out, you remain in <strong>read-only mode</strong>:
        you can browse surveys, posts, replies, and analytics, but interactive actions (responding, liking, posting, replying,
        or creating surveys) are disabled.
      </p>

      <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Current Status</h3>
        {loading ? (
          <div>Loading…</div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            Anonymous user ID is currently <strong>{consented ? 'enabled (full access)' : 'disabled (read-only mode)'}</strong>.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn"
            type="button"
            onClick={handleOptOut}
            disabled={loading || updating || !consented}
            style={{ background: !consented ? '#e5e7eb' : '#f97316', color: '#111827' }}
          >
            Opt out (read-only)
          </button>
          <button
            className="btn"
            type="button"
            onClick={handleOptIn}
            disabled={loading || updating || consented}
            style={{ background: consented ? '#e5e7eb' : '#2563eb', color: consented ? '#111827' : '#fff' }}
          >
            Re-enable anonymous ID
          </button>
        </div>
        {(error || message) && (
          <div className="muted" role="status" style={{ marginTop: 10 }}>{error || message}</div>
        )}
      </div>

      <p style={{ marginTop: 16 }}>
        Questions about how we process anonymous IDs? Contact
        {' '}<a href="mailto:supports@getsurvey.app">supports@getsurvey.app</a>.
      </p>
      <p className="muted" style={{ marginTop: 8 }}>
        When you’re finished here, use your browser’s Back button (or close this window) and refresh the previous page
        so surveys reload with full access.
      </p>
    </div>
  );
}
